import Kichiri from "./KichiriService";
import UserService from "./UserService";
import UtilityService from "./UtilityService";
import ClientService from "./ClientService";
import ConfigurationService from "./ConfigurationService";
import { authedFetch, readFileAnswer, COULD_NOT_REACH_THE_SERVER } from "./apiFetch";
import CurrentYearMovementsService from "./CurrentYearMovementsService";
import BookkeepingService from "./BookkeepingService";
import moment from "moment";

function computeClosingBalance({ accountNumber, current, opening, movements, aje }) {
	if (accountNumber === "NETINC") return current;
	return opening + movements + aje;
}

function columnsTheBooksKept({ account }) {
	const theBooksKeptThem = typeof account.movements === "number";
	if (!theBooksKeptThem) return { opening: null, movements: null, aje: null };
	return { opening: account.opening || 0, movements: account.movements, aje: account.aje || 0 };
}

function buildCanonicalAccountRow({ account }) {
	const columns = columnsTheBooksKept({ account });
	const closing = account.current || 0;
	const prior = account.prior || 0;
	const changePercent = UtilityService.calculatePercentChange({ from: prior, to: closing });
	return { account, description: account.description, accountNumber: account.accountNumber, opening: columns.opening, movements: columns.movements, aje: columns.aje, closing, prior, changePercent };
}

function buildScheduleAccountRow({ account, isBalanceSheet, bankMovementsByAccount, ajeMovementsByAccount }) {
	const sign = account.sign || 1;
	const opening = isBalanceSheet ? (account.prior || 0) : 0;
	const rawBankMovement = bankMovementsByAccount[account.accountNumber] || 0;
	const rawAjeMovement = ajeMovementsByAccount[account.accountNumber] || 0;
	const movements = rawBankMovement * sign;
	const aje = rawAjeMovement * sign;
	const closing = computeClosingBalance({ accountNumber: account.accountNumber, current: account.current, opening, movements, aje });
	const prior = account.prior || 0;
	const changePercent = UtilityService.calculatePercentChange({ from: prior, to: closing });
	return { account, description: account.description, accountNumber: account.accountNumber, opening, movements, aje, closing, prior, changePercent };
}

function buildAccountRow({ account, isBalanceSheet, bankMovementsByAccount, ajeMovementsByAccount }) {
	const isCanonical = !account.schedule;
	if (isCanonical) return buildCanonicalAccountRow({ account });
	return buildScheduleAccountRow({ account, isBalanceSheet, bankMovementsByAccount, ajeMovementsByAccount });
}

function buildSectionRows({ section, isBalanceSheet, bankMovementsByAccount, ajeMovementsByAccount }) {
	const accounts = section.accounts.map(account => buildAccountRow({ account, isBalanceSheet, bankMovementsByAccount, ajeMovementsByAccount }));
	return { name: section.name, accounts };
}

const AFSService = {
	/**
	 * The accounts an adjusting entry line can post to. Each option names the chart account it is
	 * (when the client has a chart) and always the statement line it lands on, so a saved line can
	 * be placed on the statements and, for a QuickBooks client, booked to the linked QuickBooks
	 * account. Accounts without a number are offered like any other; accounts with no statement
	 * line yet are left out, because an entry to them could not be placed.
	 *
	 * @param {Object} params
	 * @param {Array} params.chartOfAccounts - rows from getClientCoA: client chart rows carry id, or taxonomy rows when the client has no chart
	 * @return {Array} [{ key, chartAccountId, canonicalCode, accountNumber, label, gifiCode }]
	 */
	describeAdjustingEntryAccountOptions({ chartOfAccounts }) {
		const rows = (chartOfAccounts || []).filter(row => row && row.canonical_code && !row.isRetired);
		const options = rows.map(row => ({
			key: row.id ? `chart:${row.id}` : `code:${row.canonical_code}`,
			chartAccountId: row.id || null,
			canonicalCode: row.canonical_code,
			accountNumber: String(row.accountNumber || "").trim(),
			label: BookkeepingService.describeChartAccount({ account: row }),
			gifiCode: row.gifi_code || null,
		}));
		return options;
	},

	/**
	 * The date to offer for a compilation report that has none yet: today, or the day after the
	 * year-end when the year has not ended — a report cannot be dated inside the period it covers.
	 *
	 * @param {Object} params
	 * @param {String} params.yearEnd - YYYY-MM-DD
	 * @return {String} YYYY-MM-DD
	 */
	suggestCompilationReportDate({ yearEnd }) {
		const today = moment().format("YYYY-MM-DD");
		if (!yearEnd || today > yearEnd) return today;
		const dayAfterYearEnd = moment(yearEnd, "YYYY-MM-DD").add(1, "day").format("YYYY-MM-DD");
		return dayAfterYearEnd;
	},

	/**
	 * What the review found behind an out-of-balance balance sheet — the balance-sheet check's
	 * recommended step, which names the off-statement balance that equals the gap — or null when the
	 * cause is not known.
	 *
	 * @param {Object} params
	 * @param {Array} params.crossCheckResults
	 * @return {String|null}
	 */
	describeWhatExplainsTheImbalance({ crossCheckResults }) {
		const balanceCheck = (crossCheckResults || []).find(result => result.id === "BS_01");
		if (!balanceCheck || balanceCheck.passed) return null;
		const steps = (balanceCheck.resolution && balanceCheck.resolution.steps) || [];
		const recommended = steps.find(step => step.recommended);
		return recommended ? recommended.text : null;
	},

	/**
	 * The option an adjusting entry line was posted to, found by the chart account it stores, then
	 * by its statement line when it has no chart account. Lines saved before chart accounts were
	 * stored are matched by neither and return null, so the caller shows the saved description.
	 *
	 * @param {Object} params
	 * @param {Array} params.options - from describeAdjustingEntryAccountOptions
	 * @param {Object} params.line - { chartAccountId, canonicalCode }
	 * @return {Object|null}
	 */
	findAdjustingEntryAccountOption({ options, line }) {
		if (line.chartAccountId) return options.find(option => String(option.chartAccountId) === String(line.chartAccountId)) || null;
		if (line.canonicalCode) return options.find(option => !option.chartAccountId && option.canonicalCode === line.canonicalCode) || null;
		return null;
	},

	/**
	 * One opened statement line, in the one shape every opener produces and the detail panel reads:
	 * what it is called, which line it is, where its balance came from when the statements present it
	 * on the other side, and the figures as the statement shows them.
	 *
	 * @param {Object} params
	 * @param {String} params.title
	 * @param {String} params.canonicalCode
	 * @param {String|null} params.reclassifiedFrom - the line the balance came from, when presented on the other side
	 * @param {String|null} params.presentedAs - the line this balance is presented as, when it sits on the other side
	 * @param {Number} params.current
	 * @param {Number} params.prior
	 * @return {Object} { kind: "line", title, canonicalCode, reclassifiedFrom, presentedAs, current, prior }
	 */
	describeOpenedLine({ title, canonicalCode, reclassifiedFrom, presentedAs, current, prior }) {
		const line = { kind: "line", title, canonicalCode, reclassifiedFrom: reclassifiedFrom || null, presentedAs: presentedAs || null, current: current || 0, prior: prior || 0 };
		return line;
	},

	/**
	 * What opening one statement line shows: the line, where its balance came from when the
	 * statements present it on the other side, and the figures as the statement shows them.
	 *
	 * @param {Object} params
	 * @param {Object} params.account - a statement account: { description, canonical_code, reclassified_from_canonical_code, balance_presented_as_canonical_code, current, prior }
	 * @return {Object} { kind: "line", title, canonicalCode, reclassifiedFrom, presentedAs, current, prior }
	 */
	describeLineToOpen({ account }) {
		const line = this.describeOpenedLine({
			title: account.description,
			canonicalCode: account.canonical_code,
			reclassifiedFrom: account.reclassified_from_canonical_code,
			presentedAs: account.balance_presented_as_canonical_code,
			current: account.current,
			prior: account.prior,
		});
		return line;
	},

	/**
	 * What opening a total shows: the total, and each statement line it is made of, each of which
	 * can be opened in turn. Lines with no figure in either year are left out, as the statements
	 * leave them out.
	 *
	 * @param {Object} params
	 * @param {String} params.title
	 * @param {Array<Object>} params.accounts - the statement accounts behind the total
	 * @param {Number} params.current
	 * @param {Number} params.prior
	 * @return {Object} { kind: "lines", title, current, prior, lines }
	 */
	describeLinesToOpen({ title, accounts, current, prior }) {
		const linesWithAFigure = (accounts || []).filter(account => (account.current || 0) !== 0 || (account.prior || 0) !== 0);
		const total = { kind: "lines", title, current: current || 0, prior: prior || 0, lines: linesWithAFigure.map(account => this.describeLineToOpen({ account })) };
		return total;
	},

	/**
	 * What opening a statement total shows: the total, and each statement line in the sections it
	 * adds up. The one way every statement view opens a subtotal or grand total.
	 *
	 * @param {Object} params
	 * @param {String} params.title
	 * @param {Array<Object>} params.sections - the statement sections the total adds up
	 * @param {Number} params.current
	 * @param {Number} params.prior
	 * @return {Object} { kind: "lines", title, current, prior, lines }
	 */
	describeSectionsTotalToOpen({ title, sections, current, prior }) {
		const total = this.describeLinesToOpen({ title, accounts: this.listAccountsInSections({ sections }), current, prior });
		return total;
	},

	/**
	 * What opening a lead sheet account shows. A lead sheet signs a contra account the way the
	 * statement prints it, so its closing is the figure the line's parts must add up to.
	 *
	 * @param {Object} params
	 * @param {Object} params.account - a lead sheet account: { code, name, closing, prior }
	 * @return {Object} { kind: "line", title, canonicalCode, reclassifiedFrom, current, prior }
	 */
	describeLeadSheetLineToOpen({ account }) {
		const line = this.describeOpenedLine({ title: account.name, canonicalCode: account.code, reclassifiedFrom: null, presentedAs: null, current: account.closing, prior: account.prior });
		return line;
	},

	/**
	 * What opening a lead sheet's total shows: each of its accounts, each openable.
	 *
	 * @param {Object} params
	 * @param {Object} params.leadSheet - { name, accounts, totals }
	 * @return {Object} { kind: "lines", title, current, prior, lines }
	 */
	describeLeadSheetTotalToOpen({ leadSheet }) {
		const lines = (leadSheet.accounts || []).filter(account => (account.closing || 0) !== 0 || (account.prior || 0) !== 0).map(account => this.describeLeadSheetLineToOpen({ account }));
		const total = { kind: "lines", title: leadSheet.name, current: leadSheet.totals.closing || 0, prior: leadSheet.totals.prior || 0, lines };
		return total;
	},

	/**
	 * The statement accounts in a set of sections, in the order the statement shows them.
	 *
	 * @param {Object} params
	 * @param {Array<Object>} params.sections
	 * @return {Array<Object>}
	 */
	listAccountsInSections({ sections }) {
		const accounts = (sections || []).flatMap(section => section.accounts || []);
		return accounts;
	},

	/**
	 * Every statement account on both statements, in statement order.
	 *
	 * @param {Object} params
	 * @param {Object} params.statements - { balanceSheet, incomeStatement }
	 * @return {Array<Object>}
	 */
	listEveryStatementAccount({ statements }) {
		const balanceSheetSections = (statements.balanceSheet || {}).sections || [];
		const incomeStatementSections = (statements.incomeStatement || {}).sections || [];
		const accounts = this.listAccountsInSections({ sections: balanceSheetSections.concat(incomeStatementSections) });
		return accounts;
	},

	/**
	 * The statement accounts a printed total names as its lines, found by their canonical codes
	 * among the statements' own accounts.
	 *
	 * @param {Object} params
	 * @param {Array<String>} params.canonicalCodes - the total's made_of list
	 * @param {Object} params.statements - { balanceSheet, incomeStatement }
	 * @return {Array<Object>}
	 */
	findAccountsATotalIsMadeOf({ canonicalCodes, statements }) {
		const everyAccount = this.listEveryStatementAccount({ statements });
		const accounts = (canonicalCodes || []).map(code => everyAccount.find(account => account.canonical_code === code)).filter(Boolean);
		return accounts;
	},

	/**
	 * The statement account a printed line stands for, found by its canonical code and, for a
	 * balance presented on the other side, the line it came from — so opening a printed figure
	 * opens the unrounded account behind it.
	 *
	 * @param {Object} params
	 * @param {Object} params.row - a printed statement row: { canonical_code, reclassified_from_canonical_code }
	 * @param {Object} params.statements - { balanceSheet, incomeStatement }
	 * @return {Object|null}
	 */
	findAccountForPrintedLine({ row, statements }) {
		const everyAccount = this.listEveryStatementAccount({ statements });
		const cameFrom = row.reclassified_from_canonical_code || null;
		const account = everyAccount.find(candidate => candidate.canonical_code === row.canonical_code && (candidate.reclassified_from_canonical_code || null) === cameFrom) || null;
		return account;
	},

	/**
	 * How a line's figure is made, from the server's description of it: the opening, movements and
	 * adjusting entries, signed the way the statement shows the line — a balance presented on the
	 * other side of the balance sheet is shown negated, as the statement shows it.
	 *
	 * @param {Object} params
	 * @param {Object} params.line - one of statement_line_detail's lines
	 * @param {Boolean} params.presentedOnTheOtherSide
	 * @return {Object} { opening, movements, adjustments, closing, prior }
	 */
	describeHowALineIsMade({ line, presentedOnTheOtherSide }) {
		const sign = presentedOnTheOtherSide ? -1 : 1;
		const made = {
			opening: sign * line.opening,
			movements: sign * line.movements,
			adjustments: sign * line.adjustments,
			closing: sign * line.closing,
			prior: sign * line.prior,
		};
		return made;
	},

	/**
	 * A QuickBooks account's ledger said in one sentence a preparer can rely on: whether its entries
	 * reach the figure the trial balance gave the account, and if not, by how much and why.
	 *
	 * @param {Object} params
	 * @param {Object} params.ledger - qbo_account_ledger's answer
	 * @return {String}
	 */
	describeLedgerAgreement({ ledger }) {
		const closing = UtilityService.formatCurrency(ledger.closing);
		if (ledger.agreesWithTheTrialBalance) return `QuickBooks's entries close at ${closing}, the figure on its trial balance.`;
		const gap = UtilityService.formatCurrency(Math.abs(ledger.difference));
		if (ledger.differenceIsTheAutomaticClose) return `QuickBooks's entries close at ${closing}; its trial balance shows ${UtilityService.formatCurrency(ledger.trialBalanceFigure)}. The ${gap} is earlier years' profit, which QuickBooks closes into retained earnings without an entry.`;
		return `QuickBooks's entries close at ${closing} but its trial balance shows ${UtilityService.formatCurrency(ledger.trialBalanceFigure)} — ${gap} unexplained.`;
	},

	/**
	 * Builds the trial balance rows a working paper shows — every account with its
	 * opening balance, bank movements, AJE movements, closing balance, prior-year
	 * figure and the percentage change — grouped by balance sheet then income
	 * statement section. Pure: no network, no mutation of the inputs.
	 *
	 * An account the books built (a canonical account) arrives with its opening balance, its
	 * movements and its adjusting entries kept apart by the server, and the row shows exactly
	 * those, so Opening + Movements + AJE is the closing figure on every row. The closing balance
	 * used to be printed under Movements for these accounts, so Land read as having moved by its
	 * whole cost. A row the server did not describe that way shows no figure in those columns
	 * rather than a wrong one. A CaseWare-shaped account still has its movements added up here,
	 * from what each transaction posted to it and its tax to the GST/HST account, the same way the
	 * statements on that path are restated.
	 *
	 * @param {Object} balanceSheet - { sections: [{ name, accounts }] }
	 * @param {Object} incomeStatement - { sections: [{ name, accounts }] }
	 * @param {Array} transactions - Bank transactions
	 * @param {Array} adjustingEntries - AJEs with debit/credit lines
	 * @param {Object|null} [gstHstAccount] - the chart account GST/HST posts to, for the CaseWare-shaped path
	 * @returns {Array} sections: [{ name, accounts: [{ account, description, accountNumber, opening, movements, aje, closing, prior, changePercent }] }]
	 */
	buildTrialBalanceRows({ balanceSheet, incomeStatement, transactions, adjustingEntries, gstHstAccount = null }) {
		const balanceSheetSections = (balanceSheet && balanceSheet.sections) || [];
		const incomeStatementSections = (incomeStatement && incomeStatement.sections) || [];

		const bankMovementsByAccount = CurrentYearMovementsService.aggregateTransactionMovements({ transactions, gstHstAccount });
		const ajeMovementsByAccount = CurrentYearMovementsService.aggregateAdjustingEntryMovements({ adjustingEntries });

		const balanceSheetRows = balanceSheetSections.map(section => buildSectionRows({ section, isBalanceSheet: true, bankMovementsByAccount, ajeMovementsByAccount }));
		const incomeStatementRows = incomeStatementSections.map(section => buildSectionRows({ section, isBalanceSheet: false, bankMovementsByAccount, ajeMovementsByAccount }));

		const sections = [...balanceSheetRows, ...incomeStatementRows];
		return sections;
	},

	/**
	 * Identifies bank accounts from uploaded statement files
	 * @param {Number} clientId
	 * @param {Array<File>} files
	 * @return {Promise<Object>}
	 */
	async identifyBankAccounts({ clientId, files }) {
		const formData = new FormData();
		for (const f of (files || [])) {
			formData.append("files", f);
		}

		const response = await authedFetch(`/tools/bookkeeping/identify_bank_accounts?clientId=${clientId}`, {
			method: "POST",
			body: formData,
		});

		return response.json();
	},

	/**
	 * Downloads the financial statements as an Excel workbook.
	 *
	 * The server builds the workbook from the engagement's books through the same assembly the
	 * screen renders, rounded and swept the same way, so the browser names the engagement rather
	 * than sending the statements it happens to hold.
	 *
	 * A reply that is not a file is returned as the error it is and nothing is saved; the download
	 * used to save whatever came back under the file's name, so a failed export arrived as a
	 * spreadsheet Excel refused to open.
	 *
	 * @param {Number} clientId
	 * @param {Number} wpfEngagementId
	 * @return {Promise<Object>} { downloaded: true } or { error }
	 */
	async downloadAFS({ clientId, wpfEngagementId }) {
		try {
			const { client } = await ClientService.fetchClient({ clientId });

			const response = await authedFetch(`/tools/afs/download`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ clientId, wpfEngagementId }),
			});

			const { blob, error } = await readFileAnswer({ response });
			if (error) return { error };

			UtilityService.downloadFile({ blob, fileName: `${client.name} AFS.xlsx` });
			return { downloaded: true };
		} catch (error) {
			return { error: COULD_NOT_REACH_THE_SERVER };
		}
	},

	/**
	 * Full CSRS 4200 compilation pipeline — SSE streaming.
	 * Sends transactions + settings → classifies → validates → builds AFS → streams review agent.
	 *
	 * Events emitted: { type: "state", state, afs, ... } then agent tool events, then { type: "final" }
	 */
	runCompilationEngagement({ clientId, province, yearEnd, accountantName, firmName, entityType, onEvent, onState, onComplete, onError }) {
		const controller = new AbortController();
		let finished = false;
		let pausedOnSuspense = false;

		const finishWithError = message => {
			if (finished) return;
			finished = true;
			if (onError) onError(message);
		};
		const finishWithComplete = data => {
			if (finished) return;
			finished = true;
			if (onComplete) onComplete(data);
		};

		authedFetch(`/tools/afs/compilation_run`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			signal: controller.signal,
			body: JSON.stringify({
				client_id: clientId,
				province: province || undefined,
				fiscal_year_end: yearEnd || undefined,
				accountant_name: accountantName || undefined,
				firm_name: firmName || undefined,
				entity_type: entityType || undefined,
			}),
		}).then(function (response) {
			if (!response.ok) {
				response.text()
					.then(text => finishWithError(`AFS build failed (HTTP ${response.status}): ${(text || "").slice(0, 300)}`))
					.catch(() => finishWithError(`AFS build failed (HTTP ${response.status})`));
				return;
			}

			let contentType = response.headers.get("content-type") || "";

			if (contentType.includes("application/json")) {
				response.json().then(function (data) {
					if (onState) onState(data);
					finishWithComplete(data);
				}).catch(function (err) {
					finishWithError(err.message);
				});
				return;
			}

			let reader = response.body.getReader();
			let decoder = new TextDecoder();
			let buffer = "";

			function readChunk() {
				reader.read().then(function ({ done, value }) {
					if (done) {
						if (!pausedOnSuspense) {
							finishWithError("The AFS build ended before finishing — the server may have hit an error. Check the API logs.");
						}
						return;
					}

					buffer += decoder.decode(value, { stream: true });
					let lines = buffer.split("\n");
					buffer = lines.pop();

					for (let line of lines) {
						if (line.startsWith("data: ")) {
							try {
								let event = JSON.parse(line.substring(6));
								if (event.type === "final") {
									finishWithComplete(event.result);
								} else if (event.type === "error") {
									finishWithError(event.message);
								} else if (event.type === "state") {
									if (event.unresolvedItems && event.unresolvedItems.length > 0) pausedOnSuspense = true;
									if (onState) onState(event);
								} else {
									if (onEvent) onEvent(event);
								}
							} catch (e) {}
						}
					}

					readChunk();
				}).catch(function (err) {
					if (err && err.name === "AbortError") return;
					finishWithError(err.message);
				});
			}

			readChunk();
		}).catch(function (err) {
			if (err && err.name === "AbortError") return;
			finishWithError(err.message);
		});

		return {
			abort: () => {
				finished = true;
				controller.abort();
			},
		};
	},

	/**
	 * Fetches the documentation checklist for a client's engagement
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async fetchDocumentationChecklist({ clientId }) {
		let user = UserService.getUserData();
		let response = await Kichiri.tools.documentationChecklist({ client_id: clientId }, {}, user.authToken);
		return response.data;
	},

	/**
	 * Asks for a document to be checked again after an attempt that produced no answer
	 * @param {Number} documentId
	 * @return {Promise<Object>}
	 */
	async verifyDocumentAgain({ documentId }) {
		let user = UserService.getUserData();
		let response = await Kichiri.tools.verifyDocumentAgain({ documentId: documentId }, {}, user.authToken);
		return response.data;
	},

	/**
	 * Clears the agent mode state for a client
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async clearAgentMode({ clientId }) {
		let user = UserService.getUserData();
		let response = await Kichiri.tools.agentModeClear({ client_id: clientId }, {}, user.authToken);
		return response.data;
	},

	/**
	 * Build the URL for streaming a source document inline. The iframe in
	 * DocumentViewerModal points at this URL — the backend handler returns
	 * the file with Content-Disposition: inline so the browser renders it
	 * (PDFs natively, images / text inline). authToken is in the query
	 * string because iframes can't send headers.
	 */
	getDocumentViewUrl({ evidenceItemId, documentId }) {
		let user = UserService.getUserData();
		let base = `${ConfigurationService.resolveApi().rest}/tools/afs/document_view`;
		let qs = [];
		if (evidenceItemId != null) qs.push("evidenceItemId=" + encodeURIComponent(evidenceItemId));
		if (documentId != null) qs.push("documentId=" + encodeURIComponent(documentId));
		if (user && user.authToken) qs.push("authToken=" + encodeURIComponent(user.authToken));
		return base + (qs.length > 0 ? "?" + qs.join("&") : "");
	},

	/**
	 * Fetches the agent mode manifest for a client
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async fetchAgentModeManifest({ clientId }) {
		let user = UserService.getUserData();
		let response = await Kichiri.tools.agentModeManifest({ client_id: clientId }, {}, user.authToken);
		return response.data.manifest || null;
	},

	/**
	 * Runs agent mode for a client, streaming events via callbacks
	 * @param {Number} clientId
	 * @param {Function} onEvent
	 * @param {Function} onComplete
	 * @param {Function} onError
	 * @return {void}
	 */
	runAgentMode({ clientId, onEvent, onComplete, onError }) {
		authedFetch(`/tools/afs/agent_mode_run`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ client_id: clientId }),
		}).then(function (response) {
			let reader = response.body.getReader();
			let decoder = new TextDecoder();
			let buffer = "";

			function readChunk() {
				reader.read().then(function ({ done, value }) {
					if (done) return;

					buffer += decoder.decode(value, { stream: true });
					let lines = buffer.split("\n");
					buffer = lines.pop();

					for (let line of lines) {
						if (line.startsWith("data: ")) {
							try {
								let event = JSON.parse(line.substring(6));
								if (event.type === "final") {
									if (onComplete) onComplete(event.manifest);
								} else if (event.type === "error") {
									if (onError) onError(event.message);
								} else {
									if (onEvent) onEvent(event);
								}
							} catch (e) {}
						}
					}

					readChunk();
				}).catch(function (err) {
					if (onError) onError(err.message);
				});
			}

			readChunk();
		}).catch(function (err) {
			if (onError) onError(err.message);
		});
	},

	/**
	 * Downloads the compilation report as a PDF file
	 * @param {Number} clientId
	 * @param {String} entityName
	 * @param {String} yearEnd
	 * @param {Object} balanceSheet
	 * @param {Object} incomeStatement
	 * @param {Object} retainedEarnings
	 * @param {Object} compilationData
	 * @param {Object} clientProfile
	 * @param {Object} ppeNote
	 * @param {Object} noteTexts
	 * @param {Array} noteIds
	 * @return {Promise<Object>} { downloaded: true } or { error }
	 */
	async downloadPDF({ clientId, entityName, yearEnd, balanceSheet, incomeStatement, retainedEarnings, compilationData, clientProfile, ppeNote, noteTexts, noteIds, customNoteTitles }) {
		try {
			let { client } = await ClientService.fetchClient({ clientId });

			let response = await authedFetch(`/tools/afs/download_pdf`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					clientId,
					entityName: entityName || client.name,
					yearEnd,
					balanceSheet,
					incomeStatement,
					retainedEarnings,
					compilationData,
					clientProfile,
					ppeNote,
					noteTexts: noteTexts || {},
					noteIds: noteIds || undefined,
					customNoteTitles: customNoteTitles || undefined,
				}),
			});

			const { blob, error } = await readFileAnswer({ response });
			if (error) return { error };

			UtilityService.downloadFile({ blob, fileName: `${client.name} - Compilation ${yearEnd || "AFS"}.pdf` });
			return { downloaded: true };
		} catch (error) {
			return { error: COULD_NOT_REACH_THE_SERVER };
		}
	},

	/**
	 * Generates an AI review of the AFS financial statements
	 * @param {Number} clientId
	 * @param {String} entityName
	 * @param {String} yearEnd
	 * @param {Object} balanceSheet
	 * @param {Object} incomeStatement
	 * @param {Object} retainedEarnings
	 * @return {Promise<Object>}
	 */
	async generateReview({ clientId, entityName, yearEnd, balanceSheet, incomeStatement, retainedEarnings }) {
		try {
			let user = UserService.getUserData();
			let response = await Kichiri.tools.reviewAFS({ clientId, entityName, yearEnd, balanceSheet, incomeStatement, retainedEarnings }, {}, user.authToken);
			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Retrieves the compliance checklist for a client
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async getComplianceChecklist({ clientId }) {
		try {
			let user = UserService.getUserData();
			let response = await Kichiri.tools.complianceChecklist({ client_id: clientId }, {}, user.authToken);
			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Sets or clears a compliance evidence value for a client
	 * @param {Number} clientId
	 * @param {String} storage_key
	 * @param {*} value
	 * @param {Boolean} clear
	 * @return {Promise<Object>}
	 */
	async setComplianceEvidence({ clientId, storage_key, value, clear = false }) {
		try {
			let user = UserService.getUserData();
			let response = await Kichiri.tools.complianceEvidence({ client_id: clientId, storage_key: storage_key, value: value, clear: clear }, {}, user.authToken);
			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Uploads a compliance document for a client
	 * @param {Number} clientId
	 * @param {String} storage_key
	 * @param {File} file
	 * @return {Promise<Object>}
	 */
	async uploadComplianceDocument({ clientId, storage_key, file }) {
		try {
			let formData = new FormData();
			formData.append("files", file);
			formData.append("clientId", clientId);
			formData.append("storage_key", storage_key);
			let response = await authedFetch(`/tools/afs/compliance_document`, {
				method: "POST",
				body: formData,
			});
			return await response.json();
		} catch (error) {
			console.log(error);
		}
		return null;
	},

	/**
	 * Deletes a compliance document for a client
	 * @param {Number} clientId
	 * @param {String} storage_key
	 * @return {Promise<Object>}
	 */
	async deleteComplianceDocument({ clientId, storage_key }) {
		try {
			let user = UserService.getUserData();
			let response = await Kichiri.tools.deleteComplianceDocument({ client_id: clientId, storage_key: storage_key }, {}, user.authToken);
			return response.data;
		} catch (error) {
			console.log(error);
		}
		return null;
	},

	/**
	 * Acknowledges or unacknowledges a compliance item for a client
	 * @param {Number} clientId
	 * @param {Number} ciId
	 * @param {Boolean} acknowledge
	 * @param {String} note
	 * @return {Promise<Object>}
	 */
	async acknowledgeCi({ clientId, ciId, acknowledge = true, note = null }) {
		try {
			let user = UserService.getUserData();
			let response = await Kichiri.tools.complianceAcknowledge({ client_id: clientId, ci_id: ciId, acknowledge: acknowledge, note: note }, {}, user.authToken);
			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},
};

export default AFSService;
